import UseContext from "../../../../../hooks/useContext";
import useModal from "../../../useModal";
import Modalheader from "../../../components/header";
import React, { useEffect, useMemo, useState } from "react";
import ModalFooter from "../../../components/footer";
import Button from "../../../../button";
import ModalBody from "../../../components/body";
import Banner from "../../../../banner/banner";
import CheckboxOptionCard from "../../../../checkboxOptionCard/CheckboxOptionCard";
import BadgeSquare from "../../../../badge/badgeSquare";
import {
  ChevronDown,
  ChevronRight,
  ChevronTop,
  Lightning,
  Signer,
  Warning,
} from "../../../../../assets/icons";
import ApiDatabase from "server";
import { toast } from "react-toastify";
import { ScheduleChangeBeforeAfter } from "../../../../scheduleCompact/ScheduleCompactDisplay";
import { dateToIsoString, dateWordShort, parseCalendarDate } from "../../../../../helper/helper";
import { formatMissionChangeValue } from "helper/missionChangeValue";
import StepPills from "components/stepPills/StepPills";
import StyledInput from "components/styledInput/StyledInput";
import {
  getInterruptEndDateError,
  getReplacementRange,
  isWithinTrialPeriod,
} from "helper/missionInterruptDates";
import { isContractImpactingChange } from "helper/missionFieldImpact";
import { closureReasonsFor } from "helper/closureReasons";
import {
  CONTRACT_CLOSURE_INCOMPLETE,
  isContractClosureIncomplete,
} from "helper/contractClosure";
import {
  describeContractOutcome,
  describeDatePatch,
  describeStartChange,
  isOnlyTheInitialContract,
  prolongationCount,
  contractStateOf,
  CONTRACT_STATE,
} from "helper/missionContractOutcome";

const FIELD_LABELS = {
  qualification: "Qualification",
  period: "Période",
  schedule: "Planning",
  salary: "Rémunération",
  address: "Adresse",
  reason: "Motif",
  licences: "Permis",
  languages: "Langues",
  tools: "Matériel",
  reference: "Référence",
  other: "Informations pour le candidat",
  other_interim: "Informations pour l'agence",
};

function formatPeriod(val) {
  if (!val) return "";
  if (val.start && val.end) {
    return `${dateWordShort(val.start)} → ${dateWordShort(val.end)}`;
  }
  return String(val);
}

// JBWEB-1089: the two steps of a confirmation that opens a prolongation request.
const PROLONGATION_STEPS = [
  { key: "recap", label: "Récapitulatif" },
  { key: "prolongation", label: "Prolongation" },
];

// JBWEB-1089: the two steps of a confirmation that ends the mission early.
const INTERRUPTION_STEPS = [
  { key: "recap", label: "Récapitulatif" },
  { key: "interruption", label: "Interruption" },
];

// JBWEB-1089: who asks to end a mission early. Each side has its own closure
// reasons, in the database, each tied to the BeSTT motif that will close the
// contract — the value is their type.
const INTERRUPTION_REQUESTERS = [
  { value: "closure_company", label: "Entreprise" },
  { value: "closure_tempworker", label: "Intérimaire" },
];
const CLOSURE_REASON_TYPES = INTERRUPTION_REQUESTERS.map(
  (requester) => requester.value
);

// Why a change can no longer be applied to this mission, as the red card says
// it. The api refuses the same three (checkPeriodChangeConstraints).
const DUPLICATE_REASONS = {
  max_amendments:
    "Le nombre maximum d'avenants de prolongation pour cette mission est atteint (2/2). Ce changement ne peut plus être appliqué par avenant.",
  flexibility_exhausted:
    "La souplesse de cette mission a déjà été utilisée. Ce changement ne peut plus être appliqué par avenant.",
  max_duration:
    "La mission dépasserait 18 mois. Ce changement ne peut pas être appliqué à cette mission.",
};

/** The old value struck through, then the new one — "-" when it was emptied. */
function BeforeAfter({ before, after }) {
  const hasBefore = before !== null && before !== undefined && before !== "";
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {hasBefore && (
        <>
          <span className="line-through text-gray-500">{before}</span>
          <ChevronRight wh={14} color="#6B7280" />
        </>
      )}
      <span className="text-gray-900">
        {after === null || after === undefined || after === "" ? "-" : after}
      </span>
    </div>
  );
}

/**
 * One change as the design lists it: its label, then its values. A period whose
 * start did not move reads as its end date alone — which is what a prolongation
 * changes.
 */
function ChangeRow({ change, className = "" }) {
  const isPeriod = change.field === "period";
  const startMoved =
    isPeriod &&
    Boolean(change.before?.start && change.after?.start) &&
    dateWordShort(change.before.start) !== dateWordShort(change.after.start);

  const label = isPeriod
    ? startMoved
      ? "Période"
      : "Date de fin"
    : FIELD_LABELS[change.field] || change.field;

  const valueOf = (value) => {
    if (value === null || value === undefined) return null;
    if (!isPeriod) return formatMissionChangeValue(change.field, value);
    if (startMoved) return formatPeriod(value);
    return value.end ? dateWordShort(value.end) : null;
  };

  return (
    <div className={`flex flex-col gap-1 text-sm ${className}`}>
      <span className="text-gray-700">{label}</span>
      {change.field === "schedule" ? (
        <ScheduleChangeBeforeAfter before={change.before} after={change.after} />
      ) : (
        <BeforeAfter before={valueOf(change.before)} after={valueOf(change.after)} />
      )}
    </div>
  );
}

/**
 * The changes of an operation under the one thing they add up to — "Avenant
 * requis", "Appliqué immédiatement", "Nouvelle mission requise" — as the design
 * groups them. The danger tone is for a change that cannot be applied at all.
 */
function ChangesCard({ icon, title, description, tone = "neutral", changes }) {
  return (
    <div
      className={`${
        tone === "danger" ? "bg-red-50" : "bg-gray-100"
      } rounded-md p-1.5 flex flex-col`}
    >
      <div className="flex items-start gap-2 px-2.5 py-2 text-sm">
        <span className="shrink-0 mt-0.5 flex">{icon}</span>
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-gray-700">{title}</span>
          {description && (
            <span className="text-xs text-gray-600">{description}</span>
          )}
        </div>
      </div>
      <div className="bg-white rounded flex flex-col divide-y divide-gray-100">
        {changes.map((change, index) => (
          <ChangeRow key={index} change={change} className="px-3.5 py-3" />
        ))}
      </div>
    </div>
  );
}

const AMENDMENT_STATUS_MAP = {
  accepted: { type: "10", label: "Accepté" },
  refused: { type: "3", label: "Refusé" },
  replaced: { type: "18", label: "Remplacée" },
  pending: { type: "20", label: "En attente" },
};

function formatChangeValue(field, value) {
  if (field === "period" && typeof value === "object" && value !== null) {
    const start = value.start ? dateWordShort(value.start) : "?";
    const end = value.end ? dateWordShort(value.end) : "?";
    return `${start} – ${end}`;
  }
  if (field === "schedule" && Array.isArray(value)) return "—";
  if (typeof value === "object" && value !== null) return JSON.stringify(value);
  return String(value ?? "");
}

function AmendmentRequestItemCompact({ request }) {
  const [open, setOpen] = useState(false);
  const statusCfg =
    AMENDMENT_STATUS_MAP[request.status] || AMENDMENT_STATUS_MAP.pending;

  return (
    <div className="border rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full py-2 px-3 flex justify-between items-center text-xs hover:bg-gray-50 transition-colors"
      >
        <div className="flex gap-2 items-center">
          <span>
            Demande du{" "}
            {new Date(request.createdAt).toLocaleDateString("fr-FR")}
          </span>
          <BadgeSquare
            type={statusCfg.type}
            customLabel={statusCfg.label}
            size="xs"
          />
        </div>
        {open ? (
          <ChevronTop wh={16} color="#374151" />
        ) : (
          <ChevronDown wh={16} color="#374151" />
        )}
      </button>
      {open && (
        <div className="px-3 pb-2 flex flex-col gap-1 border-t bg-gray-50">
          {request.changes?.map((c, i) => (
            <div
              key={i}
              className={`flex gap-2 text-xs pt-1.5 ${
                c.field === "schedule"
                  ? "flex-col items-stretch"
                  : "items-center"
              }`}
            >
              <span className="font-medium text-gray-700">
                {FIELD_LABELS[c.field] || c.field}
              </span>
              {c.field === "schedule" ? (
                <ScheduleChangeBeforeAfter before={c.before} after={c.after} />
              ) : (
                <>
                  <span className="text-gray-500">
                    {formatChangeValue(c.field, c.before)}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className="text-gray-900 font-medium">
                    {formatChangeValue(c.field, c.after)}
                  </span>
                </>
              )}
            </div>
          ))}
          {request.respondedAt && (
            <div className="text-gray-400 text-xs pt-1">
              Répondu le{" "}
              {new Date(request.respondedAt).toLocaleDateString("fr-FR")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Calendar day of a date, whatever it was stored or sent as.
function calendarDayOf(value) {
  const d = parseCalendarDate(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Whether an earlier end falls in the negative souplesse — the window the
 * contract already allows around its term. There the term moves without a
 * rupture. Not for a replacement (imprecise term), nor once the souplesse has
 * been used. Twin of the api.
 */
function isWithinNegativeFlexibility(mission, newEnd) {
  const re = mission.replacement_employee;
  if (re && (re.firstname || re.lastname || re.poste || re.description)) {
    return false;
  }
  if (!mission.flexibility?.start || mission.flexibility.used) return false;
  const day = calendarDayOf(newEnd);
  return (
    day >= calendarDayOf(mission.flexibility.start) &&
    day < calendarDayOf(mission.duration?.dateend)
  );
}

function computeCheckResult(mission, changes) {
  if (!mission || !changes?.length) return null;

  const hasContract =
    mission.contract_tempworker?.length > 0 &&
    mission.contract_tempworker.some((c) => c.id_bestt);
  const isAssigned = !!mission.user_assigned;
  const hasPeriodChange = changes.some((c) => c.field === "period");

  if (!isAssigned || !hasContract) {
    return {
      canConfirm: true,
      noContract: isAssigned && !hasContract,
    };
  }

  // Le contrat n'est plus refait : il est patché, ou amendé. Lequel des deux se
  // décide ici plutôt que de laisser le doute à l'admin.
  if (!hasPeriodChange) {
    const outcome = describeContractOutcome({ mission, changes });

    return {
      canConfirm: true,
      banner: outcome.message
        ? { type: "info", message: outcome.message }
        : undefined,
    };
  }

  const periodChange = changes.find((c) => c.field === "period");
  const newDateEnd = parseCalendarDate(periodChange.after.end);

  // JBWEB-1144 : avancer la fin d'une mission attribuée est juridiquement une
  // rupture anticipée. Ce n'est pas une modification de champ, et l'api la
  // refuse — c'est le formulaire d'interruption qui prend le relais.
  const currentEnd = parseCalendarDate(mission.duration?.dateend);
  if (newDateEnd < currentEnd) {
    // Within the negative souplesse the term moves without a rupture: the
    // mission is updated in Jobbiz, the BeSTT contract is not. Twin of the api.
    if (isWithinNegativeFlexibility(mission, periodChange.after.end)) {
      return { canConfirm: true, negativeFlexibility: true };
    }
    // Before the mission starts there is nothing to break off: the end moves
    // in Jobbiz alone. Twin of the api.
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (today < calendarDayOf(mission.duration?.datestart)) {
      return { canConfirm: true, notStarted: true };
    }
    return {
      canConfirm: false,
      requiresInterruption: true,
      banner: {
        type: "alert",
        message:
          "Raccourcir la fin de mission d'un salarié attribué est juridiquement une rupture anticipée. Un motif est obligatoire.",
      },
    };
  }

  // Nothing signed, the initial contract alone, no hours imported: the contract
  // is corrected on BeSTT rather than amended — start or end. Twin of the api.
  const canPatchPeriod =
    contractStateOf(mission) !== CONTRACT_STATE.SIGNED &&
    isOnlyTheInitialContract(mission) &&
    !mission.has_imported_bestt_hours;

  // A new start is no prolongation: by decision, an amendment of value carrying
  // the new period. Both ends at once would be two amendments — one of them
  // awaiting the temp worker — so it is done in two steps. Twin of the api.
  const startMoved =
    Boolean(periodChange.after?.start) &&
    calendarDayOf(periodChange.after.start) !==
      calendarDayOf(mission.duration?.datestart);

  if (startMoved) {
    const newDateStart = parseCalendarDate(periodChange.after.start);
    const monthsSpanned =
      (newDateEnd.getFullYear() - newDateStart.getFullYear()) * 12 +
      (newDateEnd.getMonth() - newDateStart.getMonth());
    if (monthsSpanned > 18) {
      return {
        canConfirm: false,
        canDuplicate: true,
        duplicateReason: "max_duration",
        banner: {
          type: "alert",
          message:
            "Les dates de la mission dépassent les 18 mois : la modification n'est pas possible.",
        },
      };
    }

    if (canPatchPeriod) {
      return {
        canConfirm: true,
        banner: { type: "info", message: describeDatePatch(mission) },
      };
    }

    if (
      calendarDayOf(periodChange.after.end) !==
      calendarDayOf(mission.duration?.dateend)
    ) {
      return {
        canConfirm: false,
        banner: {
          type: "alert",
          message:
            "Le contrat est déjà engagé : modifiez d'abord la date de début, puis prolongez la mission.",
        },
      };
    }

    return {
      canConfirm: true,
      banner: {
        type: "info",
        message: describeStartChange(
          mission,
          dateWordShort(periodChange.after.start)
        ),
      },
    };
  }

  // JBWEB-1144 : une mission de remplacement n'a pas de terme fixe — elle finit
  // au retour du salarié remplacé. Elle se prolonge donc autant que nécessaire,
  // là où les autres motifs plafonnent à deux avenants, et la souplesse stockée
  // ne veut rien dire pour elle : elle a été calculée comme si le terme était
  // précis. Les 18 mois, eux, valent pour tout le monde.
  const re = mission.replacement_employee;
  const isReplacement = Boolean(
    re && (re.firstname || re.lastname || re.poste || re.description)
  );

  if (!isReplacement) {
    if (mission.flexibility?.used) {
      return {
        canConfirm: false,
        canDuplicate: true,
        duplicateReason: "flexibility_exhausted",
        banner: {
          type: "alert",
          message:
            "La souplesse a déjà été utilisée, la date de fin ne peut pas être modifiée.",
        },
      };
    }

    if (mission.flexibility?.start && mission.flexibility?.end) {
      const flexEnd = parseCalendarDate(mission.flexibility.end);
      if (newDateEnd <= flexEnd) {
        return {
          canConfirm: true,
          banner: {
            type: "info",
            message:
              "La date de fin se trouvant dans la souplesse positive, aucun avenant ne sera créé pour cette mission. La souplesse sera marquée comme utilisée.",
          },
        };
      }
    }

    // Le plafond porte sur les prolongations, pas sur les avenants en général :
    // un avenant de valeur ne renouvelle pas le terme.
    if (prolongationCount(mission) >= 2) {
      return {
        canConfirm: false,
        canDuplicate: true,
        duplicateReason: "max_amendments",
        banner: {
          type: "alert",
          borderless: true,
          message:
            "Le nombre maximum d'avenants (2) a été atteint. Vous pouvez dupliquer la mission.",
        },
      };
    }
  }

  const missionStart = parseCalendarDate(mission.duration?.datestart);
  const monthsDiff =
    (newDateEnd.getFullYear() - missionStart.getFullYear()) * 12 +
    (newDateEnd.getMonth() - missionStart.getMonth());
  if (monthsDiff > 18) {
    return {
      canConfirm: false,
      canDuplicate: true,
      duplicateReason: "max_duration",
      banner: {
        type: "alert",
        message:
          "Les dates de la mission dépassent les 18 mois, la date de fin ne peut pas être modifiée.",
      },
    };
  }

  // Nothing signed, the initial contract alone, no hours imported: nobody is
  // bound yet, so the contract is corrected rather than extended — its dates
  // are patched on BeSTT, never regenerated. The replacement follows the same
  // rule: its exemption is from the caps, not from this. Twin of the api.
  if (canPatchPeriod) {
    return {
      canConfirm: true,
      banner: { type: "info", message: describeDatePatch(mission) },
    };
  }

  if (isReplacement) {
    return {
      canConfirm: true,
      isAmendmentRequest: true,
      isReplacement: true,
      banner: {
        type: "info",
        message:
          "Le motif étant un remplacement, la mission peut être prolongée autant que nécessaire : un avenant de prolongation sera créé.",
      },
    };
  }

  return { canConfirm: true, isAmendmentRequest: true };
}

const ModalMissionDetailSubmit = () => {
  const { toggle } = useModal();
  const {
    listChangesMissionDetail,
    isChangesNeedsNewContractMissionDetail,
    infoMission,
    objID,
    setObjID,
    setModalDataReturn,
    updateMissionStatusLocal,
  } = UseContext();
  const [isLoading, setIsLoading] = useState(false);
  // JBWEB-1144 : décochée par défaut — la demande de prolongation est le cas
  // normal, l'accord donné de vive voix est l'exception.
  const [tempWorkerAlreadyAgreed, setTempWorkerAlreadyAgreed] = useState(false);
  const [step, setStep] = useState("recap");
  const [interruptionRequester, setInterruptionRequester] = useState("");
  const [interruptionReason, setInterruptionReason] = useState("");
  const [interruptionComment, setInterruptionComment] = useState("");
  const [replaceTempWorker, setReplaceTempWorker] = useState(false);
  const [closureReasons, setClosureReasons] = useState([]);
  const token = localStorage.getItem("xsrfToken");

  // JBWEB-1089: a contract nobody has signed is engaged all the same once its
  // DPAE has gone out, and only BeSTT knows that. Asked when the modal opens —
  // and only when the signatures leave it open and a change could depend on it.
  const needsDpae = useMemo(() => {
    const state = contractStateOf(infoMission);
    const unsettled =
      state === CONTRACT_STATE.GENERATED || state === CONTRACT_STATE.SENT;
    return (
      unsettled &&
      (listChangesMissionDetail || []).some(
        (change) =>
          isContractImpactingChange(change) || change.field === "schedule"
      )
    );
  }, [infoMission, listChangesMissionDetail]);
  const [dpaeTransmitted, setDpaeTransmitted] = useState(null);

  useEffect(() => {
    if (!needsDpae) return;
    ApiDatabase.getMissionDpae(
      { token, idMission: infoMission?._id },
      (data) => setDpaeTransmitted(Boolean(data?.dpaeTransmitted)),
      // Unreadable counts as not sent, as on the api.
      () => setDpaeTransmitted(false)
    );
  }, [needsDpae, infoMission?._id]);

  // The mission as the rules must read it: a DPAE, once known, marked on the
  // initial contract the way the api stores it.
  const engagedMission = useMemo(() => {
    if (!dpaeTransmitted || !infoMission) return infoMission;
    const mark = (entries = []) =>
      entries.map((entry) =>
        entry?.id_bestt && !((entry.avenant_number || 0) > 0)
          ? { ...entry, dpae_transmitted: true }
          : entry
      );
    return {
      ...infoMission,
      contract_tempworker: mark(infoMission.contract_tempworker),
      contract_company: mark(infoMission.contract_company),
    };
  }, [infoMission, dpaeTransmitted]);

  const dpaePending = needsDpae && dpaeTransmitted === null;

  const checkResult = useMemo(
    () =>
      dpaePending
        ? {
            canConfirm: false,
            banner: { type: "info", message: "Lecture de la DPAE sur BeSTT…" },
          }
        : computeCheckResult(engagedMission, listChangesMissionDetail),
    [dpaePending, engagedMission, listChangesMissionDetail]
  );

  const canConfirm = checkResult?.canConfirm !== false;
  const canDuplicate = checkResult?.canDuplicate === true;
  const requiresInterruption = checkResult?.requiresInterruption === true;

  // The closure reasons are only needed when the change ends the mission early.
  useEffect(() => {
    if (!requiresInterruption) return;
    ApiDatabase.getReasonsMissionSuspension(
      { type: CLOSURE_REASON_TYPES.join(",") },
      (data) => setClosureReasons(data || []),
      () => setClosureReasons([])
    );
  }, [requiresInterruption]);
  const banner = checkResult?.banner;

  const pendingRequest = useMemo(
    () => infoMission?.amendments?.find((a) => a.status === "pending"),
    [infoMission?.amendments]
  );

  const handleSubmit = () => {
    setIsLoading(true);
    ApiDatabase.postMissionDetail(
      {
        token,
        idMission: objID,
        // Required by the api; nothing asks for a generation from here any more.
        generateContract: Boolean(isChangesNeedsNewContractMissionDetail),
        tempWorkerAlreadyAgreed,
        changes: listChangesMissionDetail,
      },
      (data) => {
        toast.success(data.message || data.error || data.success);
        if (data.idContract) {
          setObjID(data.idContract);
        } else {
          setObjID("");
        }
        setModalDataReturn("mission updated");
        toggle();
      },
      (err) => {
        toast.error(err.response?.data?.message || err.response?.data?.error);
        setModalDataReturn("mission updated");
        toggle();
      }
    );
  };

  const handleDuplicate = () => {
    setIsLoading(true);
    const originalEnd = parseCalendarDate(infoMission?.duration?.dateend);
    const dayAfter = new Date(originalEnd);
    dayAfter.setDate(dayAfter.getDate() + 1);

    ApiDatabase.postDuplicateMission(
      {
        token,
        idMission: objID,
        dateStart: dateToIsoString(dayAfter),
        dateEnd: listChangesMissionDetail.find((c) => c.field === "period")
          ?.after?.end,
        reason: checkResult?.duplicateReason || "other",
      },
      (data) => {
        toast.success(data.success || "Mission dupliquée");
        setModalDataReturn("mission updated");
        toggle();
      },
      (err) => {
        toast.error(err.response?.data?.message || "Erreur");
        setIsLoading(false);
      }
    );
  };

  // JBWEB-1089: the other changes of the operation are applied first, under
  // their usual rules; then the mission is interrupted on its new end, which
  // closes its contract on BeSTT with the reason's motif.
  const handleInterrupt = async () => {
    const selectedReason = closureReasons.find(
      (reason) => reason._id === interruptionReason
    );
    const periodChange = listChangesMissionDetail.find(
      (change) => change.field === "period"
    );
    const otherChanges = listChangesMissionDetail.filter(
      (change) => change.field !== "period"
    );
    const newEnd = parseCalendarDate(periodChange?.after?.end);
    const replacement = getReplacementRange({
      endDate: newEnd,
      missionDateEnd: infoMission?.duration?.dateend,
    });
    const call = (method, payload) =>
      new Promise((resolve, reject) =>
        ApiDatabase[method](payload, resolve, reject)
      );

    setIsLoading(true);
    try {
      if (otherChanges.length > 0) {
        await call("postMissionDetail", {
          token,
          idMission: objID,
          generateContract: false,
          tempWorkerAlreadyAgreed: false,
          changes: otherChanges,
        });
      }
      const interruption = await call("updateMissionStatus", {
        idMission: objID,
        newStatus: "interrupted",
        reason: selectedReason?.name,
        reasonId: selectedReason?._id,
        comment: interruptionComment.trim() || undefined,
        endDate: dateToIsoString(newEnd),
        duplicateMission: replaceTempWorker,
        ...(replaceTempWorker && {
          duplicateDateStart: dateToIsoString(replacement.start),
          duplicateDateEnd: replacement.end
            ? dateToIsoString(replacement.end)
            : undefined,
        }),
      });
      updateMissionStatusLocal?.(objID, {
        status: "interrupted",
        reason: selectedReason?.name,
      });
      const done = replaceTempWorker
        ? "La mission a été interrompue, une nouvelle mission a été créée"
        : "La mission a été interrompue";
      if (isContractClosureIncomplete(interruption?.contractClosure)) {
        toast.warning(`${done}, ${CONTRACT_CLOSURE_INCOMPLETE}`);
      } else {
        toast.success(done);
      }
      setModalDataReturn("mission updated");
      toggle();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Erreur lors de l'interruption"
      );
      setIsLoading(false);
    }
  };

  const missionIsReplacement = (() => {
    const re = infoMission?.replacement_employee;
    return Boolean(
      re && (re.firstname || re.lastname || re.poste || re.description)
    );
  })();

  const duplicatedMissionBanner = infoMission?.replacement_mission ? (
    <Banner type="info">
      Cette mission a déjà été dupliquée (mission n°
      {infoMission.replacement_mission_info?.id_mission ||
        infoMission.replacement_mission}
      ).
    </Banner>
  ) : null;

  // JBWEB-1089 (design): ending the mission early is confirmed in two steps —
  // the recap, where the end date sits apart in its own red card, then the
  // interruption itself: who asks, one of that side's reasons, an optional
  // comment, and whether the client wants the temp worker replaced.
  if (requiresInterruption) {
    const onRecap = step === "recap";
    const periodChange = listChangesMissionDetail.find(
      (change) => change.field === "period"
    );
    const otherChanges = listChangesMissionDetail.filter(
      (change) => change.field !== "period"
    );
    const otherOutcome = describeContractOutcome({
      mission: engagedMission,
      changes: otherChanges,
    });
    const otherCard = otherOutcome.valueAmendment
      ? { icon: <Signer wh={16} color="#374151" />, title: "Avenant requis" }
      : otherOutcome.patch
        ? {
            icon: <Signer wh={16} color="#374151" />,
            title: "Contrat BeSTT mis à jour",
          }
        : {
            icon: <Lightning wh={16} color="#374151" />,
            title: "Appliqué immédiatement",
          };
    const newEnd = parseCalendarDate(periodChange?.after?.end);
    const endDateError = getInterruptEndDateError({
      endDate: newEnd,
      missionDateEnd: infoMission?.duration?.dateend,
    });
    const reasons = closureReasonsFor({
      reasons: closureReasons,
      requester: interruptionRequester,
      isReplacement: missionIsReplacement,
      withinTrialPeriod: isWithinTrialPeriod({
        endDate: newEnd,
        trialPeriodEnd: infoMission?.trial_period?.end,
      }),
    });
    const ready =
      Boolean(interruptionRequester) &&
      Boolean(interruptionReason) &&
      !endDateError;

    return (
      <>
        <Modalheader hide={toggle} hideShow={true}>
          {onRecap ? "Confirmer la modification" : "Interrompre la mission"}
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-500">
              {onRecap
                ? "Ces modifications prendront effet à la confirmation et seront visibles côté entreprise et intérimaire."
                : "Raccourcir la fin de mission d'un salarié attribué est juridiquement une rupture anticipée de la mission. Un motif est obligatoire."}
            </p>

            <StepPills
              steps={INTERRUPTION_STEPS}
              current={step}
              onSelect={setStep}
            />

            {onRecap ? (
              <>
                {duplicatedMissionBanner}
                {periodChange && (
                  <ChangesCard
                    tone="danger"
                    icon={<Warning wh={16} color="#DC2626" />}
                    title="Interruption de la mission"
                    description="Un formulaire de rupture anticipée de la mission devra être rempli à l'étape suivante."
                    changes={[periodChange]}
                  />
                )}
                {otherChanges.length > 0 && (
                  <ChangesCard
                    icon={otherCard.icon}
                    title={otherCard.title}
                    changes={otherChanges}
                  />
                )}
              </>
            ) : (
              <>
                <StyledInput
                  type="select"
                  label="Qui demande ?"
                  placeholder="Sélectionner"
                  value={interruptionRequester}
                  onChange={(e) => {
                    setInterruptionRequester(e.target.value);
                    // Each side has its own reasons: one chosen for the other
                    // side no longer applies.
                    setInterruptionReason("");
                  }}
                  list={INTERRUPTION_REQUESTERS}
                />
                <StyledInput
                  type="select"
                  label="Raison"
                  placeholder="Sélectionner"
                  value={interruptionReason}
                  onChange={(e) => setInterruptionReason(e.target.value)}
                  list={reasons.map((reason) => ({
                    value: reason._id,
                    label: reason.name,
                  }))}
                />
                <StyledInput
                  type="textarea"
                  label="Commentaire (optionnel)"
                  value={interruptionComment}
                  onChange={(e) => setInterruptionComment(e.target.value)}
                />
                {endDateError && (
                  <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg">
                    {endDateError}
                  </div>
                )}
                <CheckboxOptionCard
                  id="modal-replace-temp-worker"
                  checked={replaceTempWorker}
                  onChange={(e) => setReplaceTempWorker(e.target.checked)}
                  title="Remplacer l'intérimaire"
                  description="Le client a demandé à ce que l'intérimaire soit remplacé"
                />
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          {isLoading ? (
            <>
              <Button size="LG" color="DISABLED">
                Annuler
              </Button>
              <Button size="LG" color="DISABLED">
                Interruption en cours ...
              </Button>
            </>
          ) : (
            <>
              <Button size="LG" color="SECONDARY" onClick={toggle}>
                Annuler
              </Button>
              {onRecap ? (
                <Button
                  size="LG"
                  color="PRIMARY"
                  onClick={() => setStep("interruption")}
                >
                  Suivant
                </Button>
              ) : (
                <Button
                  size="LG"
                  color={ready ? "PRIMARY" : "DISABLED"}
                  onClick={ready ? handleInterrupt : undefined}
                >
                  {replaceTempWorker ? "Créer une mission" : "Confirmer"}
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </>
    );
  }

  // JBWEB-1089 (design): a change that opens a prolongation request is confirmed
  // in two steps — the recap of everything that changes, then the prolongation
  // itself, where the admin says whether the temp worker already agreed.
  if (checkResult?.isAmendmentRequest) {
    const onRecap = step === "recap";
    const periodChange = listChangesMissionDetail.find(
      (change) => change.field === "period"
    );

    return (
      <>
        <Modalheader hide={toggle} hideShow={true}>
          {onRecap ? "Confirmer la modification" : "Demande de prolongation"}
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-500">
              {onRecap
                ? "Ces modifications prendront effet à la confirmation et seront visibles côté entreprise et intérimaire."
                : checkResult.isReplacement
                  ? "La mission est un remplacement : elle se prolonge autant que nécessaire. Un avenant sera nécessaire pour confirmer ce changement."
                  : "La date de fin de la mission a été modifiée hors souplesse. Un avenant sera nécessaire pour confirmer ce changement."}
            </p>

            <StepPills
              steps={PROLONGATION_STEPS}
              current={step}
              onSelect={setStep}
            />

            {onRecap ? (
              <>
                {duplicatedMissionBanner}
                <ChangesCard
                  icon={<Signer wh={16} color="#374151" />}
                  title="Avenant requis"
                  changes={listChangesMissionDetail}
                />
              </>
            ) : (
              <>
                {periodChange && (
                  <div className="bg-gray-100 rounded-md px-3.5 py-3">
                    <ChangeRow change={periodChange} />
                  </div>
                )}
                {pendingRequest && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-600">
                      Demande en cours, qui sera annulée et remplacée :
                    </span>
                    <AmendmentRequestItemCompact request={pendingRequest} />
                  </div>
                )}
                <CheckboxOptionCard
                  id="modal-temp-worker-already-agreed"
                  checked={tempWorkerAlreadyAgreed}
                  onChange={(e) => setTempWorkerAlreadyAgreed(e.target.checked)}
                  title="L'intérimaire a déjà accepté la prolongation"
                  description="À l'oral, par téléphone ou tout autre échange en dehors de l'application"
                />
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          {isLoading ? (
            <>
              <Button size="LG" color="DISABLED">
                Annuler
              </Button>
              <Button size="LG" color="DISABLED">
                Modification de la mission ...
              </Button>
            </>
          ) : (
            <>
              <Button size="LG" color="SECONDARY" onClick={toggle}>
                Annuler
              </Button>
              {onRecap ? (
                <Button
                  size="LG"
                  color="PRIMARY"
                  onClick={() => setStep("prolongation")}
                >
                  Suivant
                </Button>
              ) : (
                <Button size="LG" color="PRIMARY" onClick={handleSubmit}>
                  Confirmer
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </>
    );
  }

  // JBWEB-1089 (design): a change the mission can no longer take — prolongations
  // capped, souplesse spent, 18 months — goes to a new mission. The card says why
  // and lists what would have changed. Re-inviting the temp worker is no longer
  // offered: the duplicate starts without one.
  if (canDuplicate) {
    return (
      <>
        <Modalheader hide={toggle} hideShow={true}>
          Confirmer la modification
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-500">
              Ces modifications prendront effet à la confirmation et seront
              visibles côté entreprise et intérimaire.
            </p>
            {duplicatedMissionBanner}
            <ChangesCard
              tone="danger"
              icon={<Warning wh={16} color="#DC2626" />}
              title="Nouvelle mission requise"
              description={DUPLICATE_REASONS[checkResult.duplicateReason]}
              changes={listChangesMissionDetail}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          {isLoading ? (
            <>
              <Button size="LG" color="DISABLED">
                Annuler
              </Button>
              <Button size="LG" color="DISABLED">
                Duplication en cours ...
              </Button>
            </>
          ) : (
            <>
              <Button size="LG" color="SECONDARY" onClick={toggle}>
                Annuler
              </Button>
              <Button size="LG" color="PRIMARY" onClick={handleDuplicate}>
                Dupliquer la mission
              </Button>
            </>
          )}
        </ModalFooter>
      </>
    );
  }

  // JBWEB-1089 (design): a change that touches Jobbiz alone — no contract yet,
  // or only fields the contract does not carry — is listed and applied, with
  // nothing to decide. Generating a contract is no longer offered from here.
  const appliedImmediately =
    Boolean(checkResult) &&
    checkResult.canConfirm !== false &&
    !checkResult.banner &&
    !checkResult.isAmendmentRequest &&
    !checkResult.canDuplicate &&
    !checkResult.requiresInterruption;

  if (appliedImmediately) {
    return (
      <>
        <Modalheader hide={toggle} hideShow={true}>
          Confirmer la modification
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-500">
              Ces modifications prendront effet à la confirmation et seront
              visibles côté entreprise et intérimaire.
            </p>
            {duplicatedMissionBanner}
            <ChangesCard
              icon={<Lightning wh={16} color="#374151" />}
              title="Appliqué immédiatement"
              description={
                checkResult.negativeFlexibility
                  ? "Utilisation de la souplesse négative : la date de fin est réduite, le contrat BeSTT n'est pas modifié. La souplesse sera marquée comme utilisée."
                  : checkResult.notStarted
                    ? "La mission n'a pas encore commencé : la date de fin est réduite, le contrat BeSTT n'est pas modifié."
                    : undefined
              }
              changes={listChangesMissionDetail}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          {isLoading ? (
            <>
              <Button size="LG" color="DISABLED">
                Annuler
              </Button>
              <Button size="LG" color="DISABLED">
                Modification de la mission ...
              </Button>
            </>
          ) : (
            <>
              <Button size="LG" color="SECONDARY" onClick={toggle}>
                Annuler
              </Button>
              <Button size="LG" color="PRIMARY" onClick={handleSubmit}>
                Confirmer
              </Button>
            </>
          )}
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Confirmer la modification
      </Modalheader>
      <ModalBody>
        <div className="text-sm text-gray-500 flex flex-col gap-3">
          <div>
            Êtes-vous sûr de vouloir modifier la mission ?
          </div>

          {duplicatedMissionBanner}

          {banner && (
            <Banner type={banner.type} borderless={banner.borderless}>
              {banner.message}
            </Banner>
          )}

          {listChangesMissionDetail.map((change, index) => (
            <div key={index} className="bg-gray-100 px-5 py-4 rounded flex flex-col gap-1">
              <span className="font-medium text-gray-700">
                {FIELD_LABELS[change.field] || change.field}
              </span>
              {change.field === "period" ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="line-through text-gray-400">
                    {formatPeriod(change.before)}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className="text-gray-800">
                    {formatPeriod(change.after)}
                  </span>
                </div>
              ) : change.field === "schedule" ? (
                <ScheduleChangeBeforeAfter
                  before={change.before}
                  after={change.after}
                />
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  {change.before !== undefined && change.before !== null && (
                    <>
                      <span className="line-through text-gray-400">
                        {formatMissionChangeValue(change.field, change.before)}
                      </span>
                      <span className="text-gray-400">→</span>
                    </>
                  )}
                  <span className="text-gray-800">
                    {formatMissionChangeValue(change.field, change.after)}
                  </span>
                </div>
              )}
            </div>
          ))}

        </div>
      </ModalBody>
      <ModalFooter>
        {isLoading ? (
          <>
            <Button size="LG" color="DISABLED">
              Annuler
            </Button>
            <Button size="LG" color="DISABLED">
              Modification de la mission ...
            </Button>
          </>
        ) : (
          <>
            <Button size="LG" color="SECONDARY" onClick={toggle}>
              Annuler
            </Button>
            <Button
              size="LG"
              color={canConfirm ? "PRIMARY" : "DISABLED"}
              onClick={canConfirm ? handleSubmit : undefined}
            >
              Confirmer
            </Button>
          </>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalMissionDetailSubmit;
